import Image from 'next/image';

import appconfig from '@/utils/appConfig';
import VehicleSpecifications from './VehicleSpecifications';
import VehicleSideFeatures from './VehicleSideFeatures';
import VehiclePricingAndCTA from './VehiclePricingAndCTA';
import { Modal } from '@/components';
import { Dispatch, SetStateAction, useState } from 'react';
import { RxDimensions } from 'react-icons/rx';
import { VEHICLE_BY_SLUG_QUERYResult } from 'sanity.types';

type VehicleData = NonNullable<VEHICLE_BY_SLUG_QUERYResult>;

interface DimensionsModalProps {
    dimensionsModalOpen: boolean;
    setDimensionsModalOpen: Dispatch<SetStateAction<boolean>>;
    vanDimensionsImageUrl: string;
    vanDimensionsImageAlt: string;
    vanDimensionsKeyImageUrl: string;
    vanDimensionsKeyImageAlt: string;
}

const DimensionsModal = ({
    dimensionsModalOpen,
    setDimensionsModalOpen,
    vanDimensionsImageUrl,
    vanDimensionsImageAlt,
    vanDimensionsKeyImageUrl,
    vanDimensionsKeyImageAlt,
}: DimensionsModalProps) => {
    return (
        <Modal
            isOpen={dimensionsModalOpen}
            onRequestClose={setDimensionsModalOpen}
        >
            {vanDimensionsImageUrl && (
                <Image
                    unoptimized={true}
                    src={vanDimensionsImageUrl}
                    alt={vanDimensionsImageAlt}
                    width={600}
                    height={400}
                    className="w-full h-auto object-cover"
                />
            )}
            {vanDimensionsKeyImageUrl && (
                <Image
                    unoptimized={true}
                    src={vanDimensionsKeyImageUrl}
                    alt={vanDimensionsKeyImageAlt}
                    width={600}
                    height={400}
                    className="w-full h-auto object-cover"
                />
            )}
        </Modal>
    );
};

interface CarDetailProps {
    vehicleData: VehicleData;
}

const VehicleDetail = ({ vehicleData }: CarDetailProps) => {
    const [dimensionsModalOpen, setDimensionsModalOpen] = useState(false);

    if (!vehicleData) return null;

    const {
        imageUrl,
        vehicleSpecifications,
        features,
        vehicleHireProps,
        _id,
        vanDimensionsImageUrl,
        vanDimensionsImageAlt,
        vanDimensionsKeyImageUrl,
        vanDimensionsKeyImageAlt,
        imageAlt,
    } = vehicleData;

    return (
        <>
            <DimensionsModal
                dimensionsModalOpen={dimensionsModalOpen}
                setDimensionsModalOpen={setDimensionsModalOpen}
                vanDimensionsImageUrl={vanDimensionsImageUrl ?? ''}
                vanDimensionsImageAlt={vanDimensionsImageAlt ?? ''}
                vanDimensionsKeyImageUrl={vanDimensionsKeyImageUrl ?? ''}
                vanDimensionsKeyImageAlt={vanDimensionsKeyImageAlt ?? ''}
            />
            <div className="mx-auto w-full max-w-[825px] mb-8">
                <div className="mx-2 bg-white border border-gray-300 rounded-md shadow-[0_2px_8px_rgba(0,0,0,.16)] overflow-hidden">
                    <div
                        className={`border-l-8  ${appconfig.colors.coloredSectionBorder}`}
                    >
                        {/* this level for link */}
                        <div className="flex border-b border-b-gray-300 flex-col sm:flex-row">
                            <div className="flex flex-col w-full">
                                <div className="pr-4 h-[330px] flex items-center justify-center relative">
                                    <Image
                                        unoptimized={true}
                                        src={imageUrl as string}
                                        alt={imageAlt || 'title'}
                                        width={384}
                                        height={217}
                                        className="mx-auto"
                                    />
                                    {vanDimensionsImageUrl ||
                                    vanDimensionsKeyImageUrl ? (
                                        <button
                                            className="absolute right-8 bottom-8 font-bold text-sm border p-2 rounded-md bg-gray-100 flex items-center"
                                            onClick={() =>
                                                setDimensionsModalOpen(true)
                                            }
                                        >
                                            <RxDimensions
                                                className="mr-2"
                                                size={20}
                                            />{' '}
                                            Dimensions
                                        </button>
                                    ) : null}
                                </div>

                                <VehicleSpecifications
                                    vehicleSpecifications={
                                        vehicleSpecifications
                                    }
                                    className="border-t border-gray-300 pt-4 pb-2"
                                />
                            </div>
                            <VehicleSideFeatures features={features} />
                        </div>

                        <VehiclePricingAndCTA
                            vehicleHireProps={vehicleHireProps}
                            vehicleId={_id}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default VehicleDetail;

export const revalidate = 5;
